import {
  type PrescriptionFileType,
  PRESCRIPTION_ACCEPTED_FILE_TYPES,
  type Treatment,
  type TreatmentDTO,
  type UserExperienceEnum,
  type PlannedTreatment,
  type PlannedRoutine,
  type PlannedSession,
  type QuestionnaireTriggerCondition,
} from "@akina/domain";
import { toCamelCase, toSnakeCase } from "@akina/utils";
import type {
  ApplyPersonalizationTemplateRequest,
  ExerciseInsight,
  ExerciseInsightApi,
  TasksSummariesDTO,
  TasksSummariesDTOApi,
  TreatmentProgressResponse,
  TreatmentProgressResponseApi,
  PersonalizationSummary,
  PersonalizationSummaryApi,
  PersonalizationResponse,
  PersonalizationResponseApi,
  CreatePersonalizationResponseApi,
  CreatePersonalizationResponse,
  PersonalizationSessionPlanRequest,
  TreatmentResponse,
  TreatmentResponseApi,
} from "types/schemas";
import { ApiMethod, type ApiConfiguration } from "../..";
import { plannedTreatmentMapper, plannedSessionMapper, plannedRoutineMapper, treatmentMapper } from "../../mappers";
import { uploadFileToS3 } from "../shared";

/*eslint-disable max-lines-per-function*/
export const treatmentApi = (config: ApiConfiguration) => ({
  async getProgressData(treatmentId: string): Promise<TreatmentProgressResponse> {
    const result = await config.httpService.fetch<TreatmentProgressResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.progressData(treatmentId),
    });

    return toCamelCase(result);
  },

  async getActiveTreatment(options?: { noCache?: boolean }): Promise<Treatment | null> {
    const result = await config.httpService.fetch<TreatmentDTO | null>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getActive,
      ...(options?.noCache && { cache: "no-store" }),
    });

    if (!result) return null;
    return treatmentMapper.toDomain(result);
  },
  async getTreatments(): Promise<Treatment[]> {
    const result = await config.httpService.fetch<TreatmentDTO[]>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.all,
    });

    return result.map(treatmentMapper.toDomain);
  },
  async updateTreatmentClinicTherapist(params: {
    therapistId?: string;
    clinicId: string;
    treatmentId: string;
  }): Promise<Treatment | null> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updateTreatmentClinicTherapist,
      data: {
        therapist_id: params.therapistId,
        organization_id: params.clinicId,
        treatment_id: params.treatmentId,
      },
    });

    return await this.getActiveTreatment();
  },
  async updateTreatmentTherapist(params: { therapistId: string; treatmentId: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updateTreatmentTherapist,
      data: {
        therapist_id: params.therapistId,
        treatment_id: params.treatmentId,
      },
    });
  },
  async updatePrescription(
    treatmentId: string,
    newPrescription: File,
    app: "care" | "portal",
  ): Promise<Treatment | null> {
    const raw = newPrescription.type;
    const fileType = (raw.split(";")[0].trim().toLowerCase() || raw) as PrescriptionFileType;
    if (!PRESCRIPTION_ACCEPTED_FILE_TYPES.includes(fileType)) {
      throw new Error(`Prescription must be one of ${PRESCRIPTION_ACCEPTED_FILE_TYPES.join(", ")}, got: ${raw}`);
    }
    // Backend upload endpoint may only accept image/jpeg and image/png; PDF may be converted or rejected by the API.

    const result = await config.httpService.fetch<{
      presigned_url: string | null;
      object_name: string | null;
    } | null>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.uploadPrescription(treatmentId, fileType),
    });

    if (!result?.presigned_url || !result?.object_name) {
      throw new Error("Prescription could not be uploaded; please try again.");
    }
    await uploadFileToS3(result.presigned_url, newPrescription);

    await this.reportPrescriptionUploadSuccess(treatmentId, result.object_name);

    if (app === "care") {
      return await this.getActiveTreatment({ noCache: true });
    }
    return null;
  },
  async reportPrescriptionUploadSuccess(treatmentId: string, prescriptionObjectName: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.prescriptionUploadSuccess,
      data: {
        treatment_id: treatmentId,
        prescription_object_name: prescriptionObjectName,
      },
    });
  },
  async getPrescriptionUrls(treatmentId: string): Promise<{
    presignedUrl: string | null;
    nextPresignedUrl: string | null;
  }> {
    const result = await config.httpService.fetch<{
      presigned_url?: string | null;
      next_presigned_url?: string | null;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPrescriptionUrls(treatmentId),
    });

    return {
      presignedUrl: result.presigned_url ?? null,
      nextPresignedUrl: result.next_presigned_url ?? null,
    };
  },
  async tasksSummaries(therapistId: string): Promise<TasksSummariesDTO> {
    const result = await config.httpService.fetch<TasksSummariesDTOApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.tasksSummaries(therapistId),
    });

    return toCamelCase(result);
  },
  async getPersonalizations(): Promise<PersonalizationSummary[]> {
    const response = await config.httpService.fetch<PersonalizationSummaryApi[]>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPersonalizations,
    });

    return toCamelCase(response);
  },
  async createPersonalization(templateKey: string): Promise<CreatePersonalizationResponse> {
    const result = await config.httpService.fetch<CreatePersonalizationResponseApi>({
      method: ApiMethod.POST,
      url: config.endpoints.treatments.createPersonalization,
      data: { treatment_template_key: templateKey },
    });
    return toCamelCase(result);
  },
  async getPersonalization(id: string): Promise<PersonalizationResponse> {
    const response = await config.httpService.fetch<PersonalizationResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPersonalization(id),
    });

    return toCamelCase(response);
  },
  async updatePersonalization(id: string, name: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePersonalization(id),
      data: { name },
    });
  },
  async deletePersonalization(id: string): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.DELETE,
      url: config.endpoints.treatments.deletePersonalization(id),
    });
  },
  async applyPersonalization(data: ApplyPersonalizationTemplateRequest): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.applyPersonalization,
      data: toSnakeCase(data),
    });
  },
  async updatePersonalizationSessionPlan(data: PersonalizationSessionPlanRequest): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePersonalizationSessionPlan,
      data: toSnakeCase(data),
    });
  },
  async updateGoal(params: { treatmentId: string; goal: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.editGoal,
      data: {
        treatment_id: params.treatmentId,
        goal: params.goal,
      },
    });

    return params.treatmentId;
  },
  async updateDiagnosis(params: { treatmentId: string; diagnosis: string }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.editDiagnosis,
      data: {
        treatment_id: params.treatmentId,
        diagnosis: params.diagnosis,
      },
    });
  },
  async updatePhase(params: { treatmentId: string; targetPhaseKey: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePhase,
      data: {
        treatment_id: params.treatmentId,
        target_phase_key: params.targetPhaseKey,
      },
    });

    return params.treatmentId;
  },
  async disconnect(params: { treatmentId: string }): Promise<string> {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.disconnect,
      data: {
        treatment_id: params.treatmentId,
      },
    });

    return params.treatmentId;
  },
  async submitHappinessFeedback({
    rating,
    comments,
    treatmentId,
    triggerCondition,
    softwareVersion,
  }: {
    rating: UserExperienceEnum;
    comments: string;
    treatmentId: string;
    triggerCondition: QuestionnaireTriggerCondition;
    softwareVersion?: string;
  }): Promise<void> {
    await config.httpService.fetch({
      method: ApiMethod.POST,
      url: config.endpoints.treatments.happinessFeedback(treatmentId),
      data: {
        training_experience: rating,
        comments: comments,
        trigger_condition: triggerCondition,
        software_version: softwareVersion,
      },
    });
  },
  async getDisplayHappinessQuestionnaire({
    treatmentId,
  }: {
    treatmentId: string;
  }): Promise<{ triggerQuestionnaire: boolean; triggerCondition: QuestionnaireTriggerCondition }> {
    const result = await config.httpService.fetch<{
      trigger_questionnaire: boolean;
      trigger_condition: QuestionnaireTriggerCondition;
    }>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.displayHappinessQuestionnaire(treatmentId),
    });

    return {
      triggerQuestionnaire: result.trigger_questionnaire,
      triggerCondition: result.trigger_condition,
    };
  },
  async extendTreatment({
    treatmentId,
    newPrescription,
    isSelfPayer,
  }: {
    treatmentId: string;
    newPrescription?: File;
    isSelfPayer: boolean;
  }): Promise<void> {
    const result = await config.httpService.fetch<{
      presigned_url: string;
      object_name: string;
    } | null>({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.extendTreatment,
      data: {
        treatment_id: treatmentId,
        prescription_mime_type: newPrescription ? newPrescription.type : null,
        is_self_payer: isSelfPayer,
      },
    });
    if (newPrescription) {
      if (!result?.presigned_url || !result?.object_name) {
        throw new Error("Prescription could not be uploaded; please try again.");
      }
      await uploadFileToS3(result.presigned_url, newPrescription);
      await this.reportPrescriptionUploadSuccess(treatmentId, result.object_name);
    }
  },
  async getExerciseInsights(treatmentId: string): Promise<ExerciseInsight[]> {
    const result = await config.httpService.fetch<ExerciseInsightApi[]>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.exerciseInsights(treatmentId),
    });
    return toCamelCase(result);
  },

  async getPlannedTreatment(treatmentId: string): Promise<TreatmentResponse> {
    const result = await config.httpService.fetch<TreatmentResponseApi>({
      method: ApiMethod.GET,
      url: config.endpoints.treatments.getPlannedTreatment(treatmentId),
    });
    return toCamelCase(result);
  },

  async updatePlannedTreatment(plannedTreatment: PlannedTreatment) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedTreatment,
      data: plannedTreatmentMapper.toDTO(plannedTreatment),
    });
  },

  async updatePlannedRoutine(plannedRoutine: PlannedRoutine) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedRoutine,
      data: plannedRoutineMapper.toDTO(plannedRoutine),
    });
  },

  async updatePlannedSession(plannedSession: PlannedSession) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.updatePlannedSession,
      data: plannedSessionMapper.toDTO(plannedSession),
    });
  },

  async resetPlannedTreatment(treatmentId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedTreatment,
      data: toSnakeCase({ treatmentId }),
    });
  },

  async resetPlannedSession(sessionId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedSession,
      data: toSnakeCase({ sessionId }),
    });
  },

  async resetPlannedRoutine(routineId: string | null) {
    await config.httpService.fetch({
      method: ApiMethod.PATCH,
      url: config.endpoints.treatments.resetPlannedRoutine,
      data: toSnakeCase({ routineId }),
    });
  },
});

export * from "./useFetchPlannedTreatment";